import { isSymbolBacking } from '../symbols/backing';
import { isImageGraphic } from '../symbols/model';
import { isAnnotationFontFamily, ANNOTATION_FONT_WEIGHTS } from './typography';
import { supportsAuthoredItalic } from '../typography/fonts';
import { isHexColour } from '../styles/colour';
import { reconcileAnnotationLayerLegend } from './legend';
import { validAreaPartsTopology } from './areaTopology';
import {
  ANNOTATION_LIMITS,
  type Annotation,
  type AnnotationAreaPart,
  type AnnotationCoordinate,
  type AnnotationEndpointStyle,
  type AnnotationLayer,
  type AnnotationTextStyle,
  type AreaAnnotationStyle,
} from './model';

type UnknownRecord = Record<string, unknown>;
const ANNOTATION_LAYER_ID = /^anl_[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/;
const ANNOTATION_ID = /^ann_[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/;

export class AnnotationValidationError extends Error {}

function invalid(message: string): never {
  throw new AnnotationValidationError(message);
}

function record(value: unknown, label: string): UnknownRecord {
  if (!value || typeof value !== 'object' || Array.isArray(value)) invalid(`${label} is invalid.`);
  return value as UnknownRecord;
}

function keys(value: UnknownRecord, allowed: readonly string[], label: string): void {
  const actual = Object.keys(value);
  if (actual.length !== allowed.length || actual.some((key) => !allowed.includes(key))) {
    invalid(`${label} has invalid fields.`);
  }
}

function boundedNumber(value: unknown, minimum: number, maximum: number, label: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < minimum || value > maximum) {
    invalid(`${label} is outside the supported range.`);
  }
  return value;
}

function authoredHeight(
  item: UnknownRecord,
  field: 'heightAboveMapM' | 'labelHeightAboveMapM',
): Record<string, number> {
  return field in item
    ? {
        [field]: boundedNumber(
          item[field],
          0,
          ANNOTATION_LIMITS.heightAboveMapM.maximum,
          'Height above map',
        ),
      }
    : {};
}

function boundedString(value: unknown, maximum: number, label: string): string {
  if (typeof value !== 'string' || !value.trim() || value.length > maximum) {
    invalid(`${label} is invalid.`);
  }
  return value;
}

function optionalLegendLabel(value: unknown, label: string): string | undefined {
  if (typeof value !== 'string' || value.length > ANNOTATION_LIMITS.nameLength)
    invalid(`${label} is invalid.`);
  return value.trim() || undefined;
}

function colour(value: unknown, label: string): string {
  if (!isHexColour(value)) invalid(`${label} is invalid.`);
  return value.toLowerCase();
}

function coordinate(value: unknown, label: string): AnnotationCoordinate {
  if (!Array.isArray(value) || value.length !== 2) invalid(`${label} is invalid.`);
  return [
    boundedNumber(value[0], -180, 180, `${label} longitude`),
    boundedNumber(value[1], -85.051129, 85.051129, `${label} latitude`),
  ];
}

function coordinates(
  value: unknown,
  minimum: number,
  polygon: boolean,
): readonly AnnotationCoordinate[] {
  if (
    !Array.isArray(value) ||
    value.length < minimum ||
    value.length > ANNOTATION_LIMITS.verticesPerAnnotation
  ) {
    invalid('Annotation coordinates are invalid.');
  }
  const parsed = value.map((item, index) => coordinate(item, `Annotation coordinate ${index + 1}`));
  if (polygon) {
    const first = parsed[0]!;
    const last = parsed.at(-1)!;
    if (first[0] !== last[0] || first[1] !== last[1])
      invalid('Annotation polygon ring must be closed.');
    const unique = new Set(parsed.slice(0, -1).map((item) => `${item[0]},${item[1]}`));
    if (unique.size < 3) invalid('Annotation polygon needs at least three distinct vertices.');
  }
  return parsed;
}

function rectangleCoordinates(value: unknown): readonly AnnotationCoordinate[] {
  const parsed = coordinates(value, 5, true);
  if (parsed.length !== 5) invalid('Rectangle annotation must contain four corners.');
  const [first, second, opposite, fourth] = parsed;
  if (
    first![1] !== second![1] ||
    second![0] !== opposite![0] ||
    opposite![1] !== fourth![1] ||
    fourth![0] !== first![0]
  ) {
    invalid('Rectangle annotation coordinates are invalid.');
  }
  return parsed;
}

function areaGeometry(item: UnknownRecord): {
  holes?: NonNullable<AnnotationAreaPart['holes']>;
  additionalParts?: readonly AnnotationAreaPart[];
} {
  // Preserve the existing validation contract for legacy, exterior-only annotations.
  if (!('holes' in item) && !('additionalParts' in item)) return {};
  if (
    'additionalParts' in item &&
    (!Array.isArray(item.additionalParts) ||
      item.additionalParts.length > ANNOTATION_LIMITS.verticesPerAnnotation / 4)
  )
    invalid('Annotation area parts are invalid.');
  let total = 0;
  const parsePart = (part: UnknownRecord): AnnotationAreaPart => {
    if ('holes' in part && !Array.isArray(part.holes)) invalid('Annotation holes are invalid.');
    total +=
      (Array.isArray(part.coordinates) ? part.coordinates.length : 0) +
      (Array.isArray(part.holes)
        ? part.holes.reduce((count, ring) => count + (Array.isArray(ring) ? ring.length : 0), 0)
        : 0);
    if (total > ANNOTATION_LIMITS.verticesPerAnnotation)
      invalid('Annotation has too many vertices.');
    const holes = Array.isArray(part.holes)
      ? part.holes.map((ring) => coordinates(ring, 4, true))
      : [];
    return {
      coordinates: coordinates(part.coordinates, 4, true),
      ...(holes.length ? { holes } : {}),
    };
  };
  const first = parsePart(item);
  const additionalParts = Array.isArray(item.additionalParts)
    ? item.additionalParts.map((value) => {
        const part = record(value, 'Annotation area part');
        keys(part, ['coordinates', ...('holes' in part ? ['holes'] : [])], 'Annotation area part');
        return parsePart(part);
      })
    : [];
  if (
    !validAreaPartsTopology(
      [first, ...additionalParts].map((part) => ({
        type: 'Polygon',
        coordinates: [part.coordinates, ...(part.holes ?? [])].map((ring) =>
          ring.map((point) => [...point]),
        ),
      })),
    )
  )
    invalid('Annotation area parts, holes or boundary are invalid.');
  return {
    ...(first.holes ? { holes: first.holes } : {}),
    ...(additionalParts.length ? { additionalParts } : {}),
  };
}

function ellipseBounds(value: unknown): readonly [AnnotationCoordinate, AnnotationCoordinate] {
  if (!Array.isArray(value) || value.length !== 2) invalid('Ellipse bounds are invalid.');
  const first = coordinate(value[0], 'Ellipse first bound');
  const second = coordinate(value[1], 'Ellipse second bound');
  if (first[0] === second[0] || first[1] === second[1])
    invalid('Ellipse bounds must have non-zero width and height.');
  return [first, second];
}

function dash(value: unknown): 'dashed' | 'solid' {
  if (value !== 'dashed' && value !== 'solid') invalid('Annotation dash style is invalid.');
  return value;
}

function opacity(value: unknown, label: string) {
  return boundedNumber(value, 0, 1, label);
}

function rotation(value: unknown): number {
  return value === undefined ? 0 : boundedNumber(value, 0, 359.999999, 'Annotation rotation');
}

function endpointStyle(value: unknown, label: string): AnnotationEndpointStyle {
  if (
    value !== 'none' &&
    value !== 'arrow' &&
    value !== 'filled-circle' &&
    value !== 'filled-square'
  )
    invalid(`${label} is invalid.`);
  return value;
}

function areaStyle(value: unknown): AreaAnnotationStyle {
  const style = record(value, 'Annotation area style');
  keys(
    style,
    ['fillColour', 'fillOpacity', 'outlineColour', 'outlineWidth', 'outlineDash'],
    'Annotation area style',
  );
  return {
    fillColour: colour(style.fillColour, 'Annotation fill colour'),
    fillOpacity: opacity(style.fillOpacity, 'Annotation fill opacity'),
    outlineColour: colour(style.outlineColour, 'Annotation outline colour'),
    outlineWidth: boundedNumber(
      style.outlineWidth,
      ANNOTATION_LIMITS.width.minimum,
      ANNOTATION_LIMITS.width.maximum,
      'Annotation outline width',
    ),
    outlineDash: dash(style.outlineDash),
  };
}

function rectangleStyle(value: unknown): Extract<Annotation, { type: 'rectangle' }>['style'] {
  const style = record(value, 'Rectangle style');
  const hasCornerRadius = 'cornerRadius' in style;
  keys(
    style,
    hasCornerRadius
      ? [
          'fillColour',
          'fillOpacity',
          'outlineColour',
          'outlineWidth',
          'outlineDash',
          'cornerRadius',
        ]
      : ['fillColour', 'fillOpacity', 'outlineColour', 'outlineWidth', 'outlineDash'],
    'Rectangle style',
  );
  return {
    ...areaStyle({
      fillColour: style.fillColour,
      fillOpacity: style.fillOpacity,
      outlineColour: style.outlineColour,
      outlineWidth: style.outlineWidth,
      outlineDash: style.outlineDash,
    }),
    cornerRadius:
      style.cornerRadius === undefined
        ? 0
        : boundedNumber(
            style.cornerRadius,
            ANNOTATION_LIMITS.cornerRadius.minimum,
            ANNOTATION_LIMITS.cornerRadius.maximum,
            'Rectangle corner radius',
          ),
  };
}

function textStyle(
  value: unknown,
  callout = false,
): AnnotationTextStyle &
  Partial<{
    leaderColour: string;
    leaderWidth: number;
    leaderDash: 'dashed' | 'solid';
    targetStyle: AnnotationEndpointStyle;
  }> {
  const style = record(value, 'Annotation text style');
  const basic = [
    'fontFamily',
    'fontSize',
    'colour',
    'alignment',
    'backgroundColour',
    'backgroundOpacity',
    'borderColour',
    'borderWidth',
    'padding',
  ];
  const optional = [
    'fontWeight',
    'fontStyle',
    'textHaloWidth',
    'textHaloColour',
    'boxHeight',
  ].filter((key) => key in style);
  basic.push(...optional);
  const withWidth = 'boxWidth' in style ? [...basic, 'boxWidth'] : basic;
  const withRadius = 'cornerRadius' in style ? [...withWidth, 'cornerRadius'] : withWidth;
  const calloutEndpoint = 'targetStyle' in style ? 'targetStyle' : 'arrowhead';
  keys(
    style,
    callout
      ? [
          ...withRadius,
          'leaderColour',
          'leaderWidth',
          'leaderDash',
          calloutEndpoint,
          ...('leaderStyle' in style ? ['leaderStyle'] : []),
          ...('leaderOpacity' in style ? ['leaderOpacity'] : []),
        ]
      : withRadius,
    'Annotation text style',
  );
  if (!isAnnotationFontFamily(style.fontFamily)) invalid('Annotation font is invalid.');
  if (
    'fontWeight' in style &&
    !ANNOTATION_FONT_WEIGHTS.some((weight) => weight === style.fontWeight)
  )
    invalid('Annotation font weight is invalid.');
  if ('fontStyle' in style && style.fontStyle !== 'normal' && style.fontStyle !== 'italic')
    invalid('Annotation font style is invalid.');
  if (!supportsAuthoredItalic(style.fontFamily) && style.fontStyle === 'italic')
    invalid('Manrope does not provide an italic face.');
  if (style.alignment !== 'left' && style.alignment !== 'centre' && style.alignment !== 'right')
    invalid('Annotation alignment is invalid.');
  const result = {
    fontFamily: style.fontFamily,
    ...('fontWeight' in style ? { fontWeight: style.fontWeight } : {}),
    ...('fontStyle' in style ? { fontStyle: style.fontStyle } : {}),
    ...('textHaloWidth' in style
      ? {
          textHaloWidth: boundedNumber(
            style.textHaloWidth,
            0,
            ANNOTATION_LIMITS.textHaloWidth.maximum,
            'Text halo width',
          ),
        }
      : {}),
    ...('textHaloColour' in style
      ? { textHaloColour: colour(style.textHaloColour, 'Text halo colour') }
      : {}),
    ...('boxHeight' in style
      ? {
          boxHeight: boundedNumber(
            style.boxHeight,
            ANNOTATION_LIMITS.textBoxHeight.minimum,
            ANNOTATION_LIMITS.textBoxHeight.maximum,
            'Text box height',
          ),
        }
      : {}),
    fontSize: boundedNumber(
      style.fontSize,
      ANNOTATION_LIMITS.fontSize.minimum,
      ANNOTATION_LIMITS.fontSize.maximum,
      'Annotation font size',
    ),
    colour: colour(style.colour, 'Annotation text colour'),
    alignment: style.alignment,
    backgroundColour: colour(style.backgroundColour, 'Annotation background colour'),
    backgroundOpacity: opacity(style.backgroundOpacity, 'Annotation background opacity'),
    borderColour: colour(style.borderColour, 'Annotation border colour'),
    borderWidth: boundedNumber(
      style.borderWidth,
      0,
      ANNOTATION_LIMITS.width.maximum,
      'Annotation border width',
    ),
    padding: boundedNumber(
      style.padding,
      ANNOTATION_LIMITS.padding.minimum,
      ANNOTATION_LIMITS.padding.maximum,
      'Annotation padding',
    ),
    boxWidth:
      style.boxWidth === undefined
        ? 180
        : boundedNumber(
            style.boxWidth,
            ANNOTATION_LIMITS.textBoxWidth.minimum,
            ANNOTATION_LIMITS.textBoxWidth.maximum,
            'Annotation text-box width',
          ),
    cornerRadius:
      style.cornerRadius === undefined
        ? 0
        : boundedNumber(
            style.cornerRadius,
            ANNOTATION_LIMITS.cornerRadius.minimum,
            ANNOTATION_LIMITS.cornerRadius.maximum,
            'Annotation corner radius',
          ),
  } as AnnotationTextStyle &
    Partial<{
      leaderColour: string;
      leaderWidth: number;
      leaderDash: 'dashed' | 'solid';
      targetStyle: AnnotationEndpointStyle;
    }>;
  if (callout) {
    const targetStyle =
      'targetStyle' in style
        ? endpointStyle(style.targetStyle, 'Callout target style')
        : typeof style.arrowhead === 'boolean'
          ? style.arrowhead
            ? 'arrow'
            : 'none'
          : invalid('Callout arrowhead setting is invalid.');
    return {
      ...result,
      ...('leaderStyle' in style
        ? {
            leaderStyle:
              style.leaderStyle === 'tapered' || style.leaderStyle === 'straight'
                ? style.leaderStyle
                : invalid('Callout leader style is invalid.'),
          }
        : {}),
      ...('leaderOpacity' in style
        ? { leaderOpacity: opacity(style.leaderOpacity, 'Callout leader opacity') }
        : {}),
      leaderColour: colour(style.leaderColour, 'Callout leader colour'),
      leaderWidth: boundedNumber(
        style.leaderWidth,
        ANNOTATION_LIMITS.width.minimum,
        ANNOTATION_LIMITS.width.maximum,
        'Callout leader width',
      ),
      leaderDash: dash(style.leaderDash),
      targetStyle,
    };
  }
  return result;
}

function annotationBase(value: UnknownRecord) {
  if (!ANNOTATION_ID.test(String(value.id))) invalid('Annotation ID is invalid.');
  if (typeof value.visible !== 'boolean') invalid('Annotation visibility is invalid.');
  const legendLabel =
    value.legendLabel === undefined
      ? undefined
      : optionalLegendLabel(value.legendLabel, 'Annotation legend label');
  return {
    id: value.id as string,
    name: boundedString(value.name, ANNOTATION_LIMITS.nameLength, 'Annotation name'),
    visible: value.visible,
    ...(value.includeInLegend === true ? { includeInLegend: true } : {}),
    ...(legendLabel ? { legendLabel } : {}),
  };
}

export function parseAnnotation(value: unknown): Annotation {
  const item = record(value, 'Annotation');
  const type = item.type;
  if ('includeInLegend' in item && typeof item.includeInLegend !== 'boolean')
    invalid('Annotation legend inclusion is invalid.');
  if ('legendLabel' in item && typeof item.legendLabel !== 'string')
    invalid('Annotation legend label is invalid.');
  const baseFields = [
    'id',
    'name',
    'visible',
    'type',
    ...('includeInLegend' in item ? ['includeInLegend'] : []),
    ...('legendLabel' in item ? ['legendLabel'] : []),
  ];
  const base = annotationBase(item);
  switch (type) {
    case 'marker': {
      keys(
        item,
        [
          ...baseFields,
          'position',
          'style',
          ...('heightAboveMapM' in item ? ['heightAboveMapM'] : []),
        ],
        'Marker annotation',
      );
      const style = record(item.style, 'Marker style');
      keys(
        style,
        [
          'shape',
          'fillColour',
          'outlineColour',
          'size',
          'opacity',
          ...['tint', 'rotationDegrees', 'anchor', 'backing'].filter((k) => k in style),
        ],
        'Marker style',
      );
      if (
        style.shape !== 'circle' &&
        style.shape !== 'square' &&
        style.shape !== 'diamond' &&
        !isImageGraphic(style.shape)
      )
        invalid('Marker shape is invalid.');
      return {
        ...base,
        type,
        position: coordinate(item.position, 'Marker position'),
        ...authoredHeight(item, 'heightAboveMapM'),
        style: {
          shape: style.shape,
          ...('backing' in style
            ? {
                backing: isSymbolBacking(style.backing)
                  ? { ...style.backing }
                  : invalid('Symbol background is invalid.'),
              }
            : {}),
          ...('tint' in style
            ? { tint: style.tint === null ? null : colour(style.tint, 'Symbol tint') }
            : {}),
          ...('rotationDegrees' in style
            ? {
                rotationDegrees: boundedNumber(style.rotationDegrees, -180, 180, 'Symbol rotation'),
              }
            : {}),
          ...('anchor' in style
            ? {
                anchor:
                  style.anchor === 'center' || style.anchor === 'bottom'
                    ? style.anchor
                    : invalid('Symbol anchor is invalid.'),
              }
            : {}),
          fillColour: colour(style.fillColour, 'Marker fill colour'),
          outlineColour: colour(style.outlineColour, 'Marker outline colour'),
          size: boundedNumber(
            style.size,
            ANNOTATION_LIMITS.markerSize.minimum,
            ANNOTATION_LIMITS.markerSize.maximum,
            'Marker size',
          ),
          opacity: opacity(style.opacity, 'Marker opacity'),
        },
      };
    }
    case 'line': {
      keys(
        item,
        [
          ...baseFields,
          'coordinates',
          'style',
          ...('heightAboveMapM' in item ? ['heightAboveMapM'] : []),
        ],
        'Line annotation',
      );
      const style = record(item.style, 'Line style');
      const endpointField = 'endStyle' in style ? 'endStyle' : 'endArrowhead';
      keys(style, ['colour', 'width', 'opacity', 'dash', endpointField], 'Line style');
      const endStyle =
        'endStyle' in style
          ? endpointStyle(style.endStyle, 'Line endpoint style')
          : typeof style.endArrowhead === 'boolean'
            ? style.endArrowhead
              ? 'arrow'
              : 'none'
            : invalid('Line arrowhead setting is invalid.');
      return {
        ...base,
        type,
        coordinates: coordinates(item.coordinates, 2, false),
        ...authoredHeight(item, 'heightAboveMapM'),
        style: {
          colour: colour(style.colour, 'Line colour'),
          width: boundedNumber(
            style.width,
            ANNOTATION_LIMITS.width.minimum,
            ANNOTATION_LIMITS.width.maximum,
            'Line width',
          ),
          opacity: opacity(style.opacity, 'Line opacity'),
          dash: dash(style.dash),
          endStyle,
        },
      };
    }
    case 'polygon':
      keys(
        item,
        [
          ...baseFields,
          'coordinates',
          'style',
          ...('holes' in item ? ['holes'] : []),
          ...('additionalParts' in item ? ['additionalParts'] : []),
        ],
        'Polygon annotation',
      );
      return {
        ...base,
        type,
        coordinates: coordinates(item.coordinates, 4, true),
        ...areaGeometry(item),
        style: areaStyle(item.style),
      };
    case 'rectangle': {
      const hasRotation = 'rotationDegrees' in item;
      keys(
        item,
        hasRotation
          ? [...baseFields, 'coordinates', 'rotationDegrees', 'style']
          : [...baseFields, 'coordinates', 'style'],
        'Rectangle annotation',
      );
      return {
        ...base,
        type,
        coordinates: rectangleCoordinates(item.coordinates),
        rotationDegrees: rotation(item.rotationDegrees),
        style: rectangleStyle(item.style),
      };
    }
    case 'ellipse': {
      const hasRotation = 'rotationDegrees' in item;
      keys(
        item,
        hasRotation
          ? [...baseFields, 'bounds', 'circleLocked', 'rotationDegrees', 'style']
          : [...baseFields, 'bounds', 'circleLocked', 'style'],
        'Ellipse annotation',
      );
      if (typeof item.circleLocked !== 'boolean') invalid('Ellipse circle lock is invalid.');
      return {
        ...base,
        type,
        bounds: ellipseBounds(item.bounds),
        circleLocked: item.circleLocked,
        rotationDegrees: rotation(item.rotationDegrees),
        style: areaStyle(item.style),
      };
    }
    case 'text':
      keys(
        item,
        [
          ...baseFields,
          'position',
          'text',
          'style',
          ...('heightAboveMapM' in item ? ['heightAboveMapM'] : []),
        ],
        'Text annotation',
      );
      return {
        ...base,
        type,
        position: coordinate(item.position, 'Text position'),
        ...authoredHeight(item, 'heightAboveMapM'),
        text: boundedString(item.text, ANNOTATION_LIMITS.textLength, 'Annotation text'),
        style: textStyle(item.style) as AnnotationTextStyle,
      };
    case 'callout': {
      keys(
        item,
        [
          ...baseFields,
          'target',
          'textPosition',
          'text',
          'style',
          ...('labelHeightAboveMapM' in item ? ['labelHeightAboveMapM'] : []),
          ...('keepLeaderVerticalOnScreen' in item ? ['keepLeaderVerticalOnScreen'] : []),
        ],
        'Callout annotation',
      );
      const style = textStyle(item.style, true);
      return {
        ...base,
        type,
        target: coordinate(item.target, 'Callout target'),
        textPosition: coordinate(item.textPosition, 'Callout text position'),
        ...authoredHeight(item, 'labelHeightAboveMapM'),
        ...('keepLeaderVerticalOnScreen' in item
          ? {
              keepLeaderVerticalOnScreen:
                typeof item.keepLeaderVerticalOnScreen === 'boolean'
                  ? item.keepLeaderVerticalOnScreen
                  : invalid('Callout screen-upright setting is invalid.'),
            }
          : {}),
        text: boundedString(item.text, ANNOTATION_LIMITS.textLength, 'Callout text'),
        style: style as Extract<Annotation, { type: 'callout' }>['style'],
      };
    }
    case 'focus-area': {
      keys(
        item,
        [
          ...baseFields,
          'coordinates',
          'style',
          ...('holes' in item ? ['holes'] : []),
          ...('additionalParts' in item ? ['additionalParts'] : []),
        ],
        'Focus area annotation',
      );
      const style = record(item.style, 'Focus area style');
      const focusStyleFields = [
        'fadeColour',
        'fadeOpacity',
        'boundaryVisible',
        'boundaryColour',
        'boundaryWidth',
        'boundaryDash',
      ];
      const hasEffectFields = 'fadeDirection' in style || 'scope' in style;
      keys(
        style,
        hasEffectFields ? [...focusStyleFields, 'fadeDirection', 'scope'] : focusStyleFields,
        'Focus area style',
      );
      if (typeof style.boundaryVisible !== 'boolean')
        invalid('Focus area boundary visibility is invalid.');
      const fadeDirection = style.fadeDirection ?? 'outside';
      const scope = style.scope ?? 'basemap-and-data';
      if (fadeDirection !== 'inside' && fadeDirection !== 'outside')
        invalid('Focus area fade direction is invalid.');
      if (scope !== 'basemap' && scope !== 'basemap-and-data')
        invalid('Focus area scope is invalid.');
      return {
        ...base,
        type,
        coordinates: coordinates(item.coordinates, 4, true),
        ...areaGeometry(item),
        style: {
          fadeColour: colour(style.fadeColour, 'Focus fade colour'),
          fadeOpacity: opacity(style.fadeOpacity, 'Focus fade opacity'),
          boundaryVisible: style.boundaryVisible,
          boundaryColour: colour(style.boundaryColour, 'Focus boundary colour'),
          boundaryWidth: boundedNumber(
            style.boundaryWidth,
            ANNOTATION_LIMITS.width.minimum,
            ANNOTATION_LIMITS.width.maximum,
            'Focus boundary width',
          ),
          boundaryDash: dash(style.boundaryDash),
          fadeDirection,
          scope,
        },
      };
    }
    default:
      invalid('Annotation type is unsupported.');
  }
}

export function parseAnnotationLayers(value: unknown): readonly AnnotationLayer[] {
  if (value === undefined) return [];
  if (!Array.isArray(value) || value.length > ANNOTATION_LIMITS.layersPerProject)
    invalid('Annotation layers are invalid.');
  const layerIds = new Set<string>();
  const annotationIds = new Set<string>();
  let projectCount = 0;
  return value.map((candidate) => {
    const layer = record(candidate, 'Annotation layer');
    keys(
      layer,
      [
        'id',
        'name',
        'visible',
        'annotations',
        ...('includeInLegend' in layer ? ['includeInLegend'] : []),
        ...('legendLabel' in layer ? ['legendLabel'] : []),
      ],
      'Annotation layer',
    );
    if (!ANNOTATION_LAYER_ID.test(String(layer.id)) || layerIds.has(layer.id as string))
      invalid('Annotation layer ID is invalid or duplicated.');
    layerIds.add(layer.id as string);
    if (
      typeof layer.visible !== 'boolean' ||
      !Array.isArray(layer.annotations) ||
      layer.annotations.length > ANNOTATION_LIMITS.annotationsPerLayer
    )
      invalid('Annotation layer is invalid.');
    projectCount += layer.annotations.length;
    if (projectCount > ANNOTATION_LIMITS.annotationsPerProject)
      invalid('The project contains too many annotations.');
    const annotations = layer.annotations.map(parseAnnotation);
    for (const annotation of annotations) {
      if (annotationIds.has(annotation.id))
        invalid('Annotation IDs must be unique across the project.');
      annotationIds.add(annotation.id);
    }
    if (annotations.filter((annotation) => annotation.type === 'focus-area').length > 1)
      invalid('An annotation layer can contain only one Focus Area.');
    if ('includeInLegend' in layer && typeof layer.includeInLegend !== 'boolean')
      invalid('Annotation-layer legend inclusion is invalid.');
    if ('legendLabel' in layer && typeof layer.legendLabel !== 'string')
      invalid('Annotation-layer legend label is invalid.');
    const legendLabel =
      layer.legendLabel === undefined
        ? undefined
        : optionalLegendLabel(layer.legendLabel, 'Annotation-layer legend label');
    return reconcileAnnotationLayerLegend({
      id: layer.id as string,
      name: boundedString(layer.name, ANNOTATION_LIMITS.nameLength, 'Annotation layer name'),
      visible: layer.visible,
      ...(layer.includeInLegend === true ? { includeInLegend: true } : {}),
      ...(legendLabel ? { legendLabel } : {}),
      annotations,
    });
  });
}
